/**
 * The pixel size of a TIFF the browser is not asked to open (bulk server route, too big to preview),
 * read from its first IFD (fix round 1, SP-1): with the real size the batch crop is re-fitted HERE
 * (applyMasterToItem) and the edge only applies fractions — it does not have to understand
 * `centreAspect`. Orientation 5-8 swaps the axes: the edge auto-orients before the ops (PLAN C4).
 * Only the header and the IFD are read, wherever the IFD sits. RAW is deliberately not probed: the
 * first IFD of a TIFF-based RAW is often the embedded preview, not the sensor image.
 */
import type { Size } from '../model/edit-stack';
import { probeSize } from '../codecs/probe-size';

async function bytes(f: Blob, from: number, to: number): Promise<Uint8Array> {
    const part = f.slice(from, Math.min(to, f.size));
    if (typeof (part as any).arrayBuffer === 'function') return new Uint8Array(await part.arrayBuffer());
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(new Uint8Array(r.result as ArrayBuffer)); r.onerror = () => rej(r.error); r.readAsArrayBuffer(part); });
}

export async function probeTiffFile(f: Blob): Promise<Size | null> {
    try {
        const h = await bytes(f, 0, 8); if (h.length < 8) return null;
        const le = h[0] === 0x49 && h[1] === 0x49; if (!le && !(h[0] === 0x4d && h[1] === 0x4d)) return null;
        const hv = new DataView(h.buffer, h.byteOffset, h.byteLength);
        if (hv.getUint16(2, le) !== 42) return null;
        const at = hv.getUint32(4, le);
        const cnt = await bytes(f, at, at + 2); if (cnt.length < 2) return null;
        const n = new DataView(cnt.buffer, cnt.byteOffset, 2).getUint16(0, le);
        const ifd = await bytes(f, at + 2, at + 2 + n * 12); if (ifd.length < n * 12) return null;
        const dv = new DataView(ifd.buffer, ifd.byteOffset, ifd.byteLength);
        let w = 0, hh = 0, o = 1;
        for (let k = 0; k < n; k++) {
            const e = k * 12; const tag = dv.getUint16(e, le), type = dv.getUint16(e + 2, le);
            const v = type === 3 ? dv.getUint16(e + 8, le) : dv.getUint32(e + 8, le);
            if (tag === 256) w = v; else if (tag === 257) hh = v; else if (tag === 274) o = v;
        }
        if (!(w > 0 && hh > 0)) return null;
        return o >= 5 && o <= 8 ? { width: hh, height: w } : { width: w, height: hh };
    } catch { return null; }
}

/**
 * The pixel size an EPS rasterises to on the edge (QA round 2, N1): ImageMagick reads it at its default
 * 72 dpi, so 1 pt of the bounding box = 1 px. `%%HiResBoundingBox` wins over `%%BoundingBox`; an
 * `(atend)` box is read from the tail; a DOS EPS binary header points at the PostScript section.
 */
export async function probeEpsFile(f: Blob): Promise<Size | null> {
    try {
        let start = 0, end = f.size;
        const h = await bytes(f, 0, 12);
        if (h.length >= 12 && h[0] === 0xc5 && h[1] === 0xd0 && h[2] === 0xd3 && h[3] === 0xc6) {
            const dv = new DataView(h.buffer, h.byteOffset, 12);
            start = dv.getUint32(4, true); end = Math.min(f.size, start + dv.getUint32(8, true));
        }
        const text = (b: Uint8Array) => { let s = ''; for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]); return s; };
        const box = (s: string): Size | null => {
            const num = '([-+]?\\d*\\.?\\d+(?:[eE][-+]?\\d+)?)';
            for (const key of ['%%HiResBoundingBox:', '%%BoundingBox:']) {
                const m = new RegExp(`^${key}\\s*${num}\\s+${num}\\s+${num}\\s+${num}`, 'm').exec(s);
                if (!m) continue;
                const w = Math.round(+m[3] - +m[1]), hh = Math.round(+m[4] - +m[2]);
                if (w > 0 && hh > 0) return { width: w, height: hh };
            }
            return null;
        };
        const head = text(await bytes(f, start, Math.min(end, start + 64 * 1024)));
        if (!/^%!PS/.test(head)) return null;
        return box(head) ?? (/^%%(HiRes)?BoundingBox:\s*\(atend\)/m.test(head) ? box(text(await bytes(f, Math.max(start, end - 64 * 1024), end))) : null);
    } catch { return null; }
}

/**
 * A server-route file the browser does not open: its real size from its header, so a single file
 * shows and crops in real pixels exactly like a batch (QA round 2 redesign: a single file IS a batch
 * of one). TIFF (IFD), EPS (bounding box), HEIC/AVIF/ICO and anything else `probeSize` reads from its
 * first MB. RAW stays null (see above): its size is read on our servers.
 */
export async function probeServerFile(f: File, format: string): Promise<Size | null> {
    if (format === 'raw') return null;
    if (format === 'tiff') return probeTiffFile(f);
    if (format === 'eps') return probeEpsFile(f);
    try { return probeSize(await bytes(f, 0, 1024 * 1024)); } catch { return null; }
}
